import React, { useEffect } from 'react';
import {
  Form as AntForm, Input, Button, Select, DatePicker, InputNumber, Row, Col,
} from 'antd';
import { IFormFieldsMeta, IFormField, EFieldType } from 'shared/types/Page.interface';
import { FileUpload } from 'shared/ui';

interface FormProps {
  fields: IFormFieldsMeta;
  onSubmit: (values: Record<string, unknown>) => void;
  onCancel?: () => void;
  initialValues?: Record<string, unknown>;
  loading?: boolean;
  submitText?: string;
  cancelText?: string;
  columns?: 1 | 2 | 3 | 4;
}

export const Form: React.FC<FormProps> = ({
  fields,
  onSubmit,
  onCancel,
  initialValues,
  columns = 1,
  loading = false,
  submitText = 'Сохранить',
  cancelText = 'Отмена',
}) => {
  const [form] = AntForm.useForm();

  const normalizeNumberInput = (value?: string): number => {
    if (!value) {
      return Number.NaN;
    }

    const sanitizedValue = value
      .replace(/,/g, '.')
      .replace(/[^\d.-]/g, '');

    const hasNegative = sanitizedValue.startsWith('-');
    const unsignedValue = hasNegative ? sanitizedValue.slice(1) : sanitizedValue;
    const [integerPart = '', ...decimalParts] = unsignedValue.split('.');
    const decimalPart = decimalParts.join('');

    const normalizedValue = `${hasNegative ? '-' : ''}${integerPart}${decimalPart ? `.${decimalPart}` : ''}`;
    const parsedValue = Number(normalizedValue);

    return Number.isNaN(parsedValue) ? Number.NaN : parsedValue;
  };

  const handleNumberKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    const allowedControlKeys = new Set([
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ]);

    if (allowedControlKeys.has(event.key) || event.ctrlKey || event.metaKey) {
      return;
    }

    if (/[a-zA-Zа-яА-Я]/.test(event.key)) {
      event.preventDefault();
    }
  };

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
    }
  }, [initialValues, form]);

  const handleSubmit = (values: Record<string, unknown>) => {
    onSubmit(values);
  };

  const renderField = (field: IFormField) => {
    const commonProps = {
      placeholder: field.placeholder,
      disabled: field.disabled,
    };

    switch (field.type) {
    case EFieldType.input:
    case EFieldType.text:
      return (
        <Input
          {...commonProps}
          allowClear={field.type === EFieldType.input ? field.allowClear : undefined}
          {...(field.mask && { mask: field.mask })}
        />
      );

    case EFieldType.textarea:
      return <Input.TextArea {...commonProps} rows={4} />;

    case EFieldType.number:
      return (
        <InputNumber
          {...commonProps}
          style={{ width: '100%' }}
          min={field.min}
          max={field.max}
          step={field.step}
          parser={normalizeNumberInput}
          onKeyDown={handleNumberKeyDown}
        />
      );

    case EFieldType.date:
      return (
        <DatePicker
          style={{ width: '100%' }}
          format={field.format || 'DD.MM.YYYY'}
          placeholder={field.placeholder}
          disabled={field.disabled}
        />
      );

    case EFieldType.dateTime:
      return (
        <DatePicker
          style={{ width: '100%' }}
          format={field.format || 'DD.MM.YYYY HH:mm'}
          showTime={field.showTime !== false}
          placeholder={field.placeholder}
          disabled={field.disabled}
        />
      );

    case EFieldType.select:
      return (
        <Select
          {...commonProps}
          mode={field.multiple ? 'multiple' : undefined}
        >
          {field.options?.map((option) => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      );

    case EFieldType.file:
      const currentFiles = form.getFieldValue(field.name) || [];

      return (
        <FileUpload
          accept={field.accept}
          maxCount={field.maxCount}
          buttonText={field.buttonText}
          buttonSize={field.buttonSize}
          fileType={field.fileType}
          uploadedFiles={currentFiles}
          onFileUploaded={(uuid) => {
            const existingFiles = form.getFieldValue(field.name) || [];
            if (Array.isArray(existingFiles)) {
              form.setFieldValue(field.name, [...existingFiles, uuid]);
            } else {
              form.setFieldValue(field.name, [uuid]);
            }
          }}
          onFileRemoved={(uuid) => {
            const existingFiles = form.getFieldValue(field.name) || [];
            if (Array.isArray(existingFiles)) {
              const updatedFiles = existingFiles.filter((fileUuid) => fileUuid !== uuid);
              form.setFieldValue(field.name, updatedFiles.length > 0 ? updatedFiles : undefined);
            } else {
              form.setFieldValue(field.name, undefined);
            }
          }}
        />
      );

    default:
      return <Input {...commonProps} />;
    }
  };

  const getFieldRules = (field: IFormField) => {
    const rules = [];

    // Автоматические правила на основе required
    if (field.required) {
      rules.push({
        required: true,
        message: `Поле "${field.label}" обязательно для заполнения`,
      });
    }

    // Дополнительные автоматические правила валидации для разных типов полей
    if (field.type === EFieldType.number) {
      rules.push({
        type: 'number' as const,
        message: `Поле "${field.label}" должно содержать число`,
      });
    }

    // Добавляем пользовательские правила, если они переданы
    if (field.rules && field.rules.length > 0) {
      rules.push(...field.rules);
    }

    return rules;
  };

  return (
    <AntForm
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={initialValues}
    >
      <Row gutter={16}>
        {fields.map((field) => (
          <Col key={field.name} span={24 / columns}>
            <AntForm.Item
              name={field.name}
              label={field.label}
              rules={getFieldRules(field)}
            >
              {field.type === EFieldType.file ? (
                <AntForm.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) => prevValues[field.name] !== currentValues[field.name]}
                >
                  {() => renderField(field)}
                </AntForm.Item>
              ) : (
                renderField(field)
              )}
            </AntForm.Item>
          </Col>
        ))}
      </Row>

      <AntForm.Item>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          {onCancel && (
            <Button onClick={onCancel} disabled={loading}>
              {cancelText}
            </Button>
          )}
          <Button type="primary" htmlType="submit" loading={loading}>
            {submitText}
          </Button>
        </div>
      </AntForm.Item>
    </AntForm>
  );
};
